import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, LogOut, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatErr } from "@/lib/api";
import { PLAN_LABEL, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PlanCards, PRICING_FALLBACK } from "@/components/sg/PlanCards";
import { Section } from "@/components/settings/Section";
import { ShowcaseSettings, LeadsInbox } from "@/components/settings/Showcase";
import { OwnerPanel } from "@/components/settings/OwnerPanel";
import { SiteAddress } from "@/components/settings/SiteAddress";
import { BrandingSettings } from "@/components/settings/Branding";
import { openCheckout } from "@/components/sg/CheckoutDialog";
import { isTrialEligible } from "@/components/sg/PayButtons";

function ProfileCard() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ display_name: user.display_name || "", company_name: user.company_name || "", ai_system_prompt: user.ai_system_prompt || "" });
  const save = async () => { await api.patch("/auth/profile", f); await refresh(); toast.success("Profile saved"); };
  const isOwner = user.role === "owner";
  return (
    <Section title="Profile" desc="Shown on invoices, emails and the client portal." testId="profile-card"
      right={isOwner && <span className="flex items-center gap-1 text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/40 rounded-full px-3 py-1" data-testid="owner-badge"><Crown className="h-3 w-3" /> OWNER</span>}>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Display name</Label><Input data-testid="settings-display-name" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} /></div>
        <div><Label>Company name</Label><Input data-testid="settings-company-name" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} /></div>
      </div>
      <div className="mt-4">
        <Label>AI assistant instructions <span className="text-muted-foreground">(optional)</span></Label>
        <Textarea data-testid="settings-ai-prompt" value={f.ai_system_prompt} onChange={(e) => setF({ ...f, ai_system_prompt: e.target.value })}
          placeholder="e.g. I'm a licensed electrician in Ohio. Keep answers short and use my standard rate of $95/hr." className="min-h-[90px]" />
      </div>
      <Button data-testid="settings-save-btn" onClick={save} className="mt-4 bg-indigo-600 hover:bg-indigo-500 text-white">Save profile</Button>
    </Section>
  );
}

function ReminderSettings() {
  const { user, setUser } = useAuth();
  const toggle = async (key, v) => { const r = await api.patch("/auth/profile", { [key]: v }); setUser(r.data); toast.success("Reminder settings saved"); };
  const rows = [
    { key: "reminders_24h", label: "24-hour reminder", hint: "Nudge the client one day after you request approval" },
    { key: "reminders_48h", label: "48-hour reminder", hint: "Second nudge two days after. Overdue alert to you at 72h." },
  ];
  return (
    <Section title="Automatic follow-ups" desc="ScopeGuard checks every 15 minutes and sends reminders for you." testId="reminder-settings">
      <div className="divide-y divide-border">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between py-3 gap-4">
            <div><div className="text-sm font-medium">{r.label}</div><div className="text-xs text-muted-foreground">{r.hint}</div></div>
            <Switch data-testid={`settings-${r.key}-switch`} checked={user[r.key] !== false} onCheckedChange={(v) => toggle(r.key, v)} />
          </div>
        ))}
      </div>
    </Section>
  );
}

function Subscription() {
  const { user, refresh } = useAuth();
  const [pricing, setPricing] = useState(PRICING_FALLBACK);
  useEffect(() => { api.get("/payments/pricing").then((r) => setPricing(r.data)); }, []);
  const plan = user.plan || "free";
  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the current billing period?")) return;
    try { await api.post("/payments/cancel"); await refresh(); toast.success("Subscription will end at the end of this period."); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Section title="Subscription" testId="subscription-card"
      desc={<>Current plan: <span className="font-semibold text-foreground" data-testid="current-plan">{PLAN_LABEL[plan]}</span>
        {user.trial_ends_at && !user.cancel_at_period_end && <span data-testid="trial-ends"> · free trial until {fmtDate(user.trial_ends_at)}, then billed automatically</span>}
        {user.cancel_at_period_end && <> · ends {fmtDate(user.plan_ends_at)}</>}
        {user.plan_source === "paypal" && <> · billed via PayPal</>}</>}>
      <PlanCards pricing={pricing} currentPlan={plan} isOwner={user.role === "owner"} trial={isTrialEligible(user)} onSelect={(key) => openCheckout(key)} />
      {user.role === "owner" && <div className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">Every premium feature is unlocked on your owner account.</div>}
      {plan !== "free" && user.role !== "owner" && !user.cancel_at_period_end && (
        <button data-testid="cancel-subscription-btn" onClick={cancel} className="mt-4 text-sm text-muted-foreground hover:text-red-600 transition-colors">Cancel subscription</button>
      )}
    </Section>
  );
}

function Account() {
  const { user, logout } = useAuth();
  const sendReset = async () => { await api.post("/auth/forgot-password", { email: user.email }); toast.success(`Password reset link sent to ${user.email}`); };
  return (
    <Section title="Account & security" desc={user.email} testId="account-card">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="settings-reset-password-btn" onClick={sendReset}><KeyRound className="h-4 w-4 mr-2" />Email me a password reset link</Button>
        <Button variant="outline" data-testid="settings-logout-btn" onClick={logout}><LogOut className="h-4 w-4 mr-2" />Sign out</Button>
      </div>
    </Section>
  );
}

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="settings-view">
      <div><h2 className="font-display text-3xl font-bold">Settings</h2><p className="text-sm text-muted-foreground mt-1">Everything saves to your account automatically.</p></div>
      {user.role === "owner" && <OwnerPanel />}
      {user.role === "owner" && <SiteAddress />}
      <ProfileCard />
      <BrandingSettings />
      <ReminderSettings />
      <ShowcaseSettings />
      <LeadsInbox />
      <Subscription />
      <Account />
    </div>
  );
}
